import { useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "home", label: t.nav.home },
    { id: "services", label: t.nav.services },
    { id: "about", label: t.nav.about },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="logo-link"
            onClick={() => handleNav("home")}
            aria-label="Oriol López — Architectorial Studio"
            className="flex items-center gap-3 group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_lopez-designs/artifacts/0j3bnuud_logo_casa_negro_fondo_transparente.png"
              alt="Architectorial studio logo"
              className="h-10 sm:h-11 w-auto object-contain"
            />
            <span className="text-lg sm:text-xl tracking-tight text-[#1A1A1A] hidden sm:inline-flex items-center gap-2 leading-none">
              <span className="font-sans font-semibold uppercase tracking-[0.18em] text-[0.95rem]">
                Architectorial
              </span>
              <span className="text-gray-300">—</span>
              <span className="font-serif">Oriol López</span>
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleNav(item.id)}
                className="link-underline text-sm font-medium tracking-wide text-[#1A1A1A] hover:text-[#52525B] transition-colors"
              >
                {item.label}
              </button>
            ))}

            {/* Language switcher */}
            <div
              data-testid="lang-switcher"
              className="flex items-center gap-1 text-sm font-medium pl-6 ml-2 border-l border-[#E5E5E5]"
            >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${
                lang === "en" ? "text-[#B89B5E]" : "text-gray-400 hover:text-[#1A1A1A]"
              }`}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
            <span className="text-gray-300">/</span>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2 py-1 transition-colors ${
                lang === "es" ? "text-[#B89B5E]" : "text-gray-400 hover:text-[#1A1A1A]"
              }`}
              aria-pressed={lang === "es"}
            >
                ES
              </button>
            </div>
          </nav>

          {/* Mobile right cluster */}
          <div className="md:hidden flex items-center gap-4">
            <div className="flex items-center gap-1 text-sm font-medium">
              <button
                data-testid="lang-en-mobile"
                onClick={() => setLang("en")}
                className={`px-1 py-1 ${lang === "en" ? "text-[#B89B5E]" : "text-gray-400"}`}
              >
                EN
              </button>
              <span className="text-gray-300">/</span>
              <button
                data-testid="lang-es-mobile"
                onClick={() => setLang("es")}
                className={`px-1 py-1 ${lang === "es" ? "text-[#B89B5E]" : "text-gray-400"}`}
              >
                ES
              </button>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex flex-col items-end justify-center gap-[5px] w-7 h-7"
            >
              <span
                className={`block h-px bg-[#1A1A1A] transition-all duration-300 ${
                  mobileOpen ? "w-6 translate-y-[6px] rotate-45" : "w-6"
                }`}
              />
              <span
                className={`block h-px bg-[#1A1A1A] transition-all duration-300 ${
                  mobileOpen ? "opacity-0" : "w-4 opacity-100"
                }`}
              />
              <span
                className={`block h-px bg-[#1A1A1A] transition-all duration-300 ${
                  mobileOpen ? "w-6 -translate-y-[6px] -rotate-45" : "w-6"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-white border-b border-[#E5E5E5] ${
          mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-8 gap-6">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-mobile-${item.id}`}
              onClick={() => handleNav(item.id)}
              className="text-left text-xl font-medium text-[#1A1A1A] hover:text-[#52525B] transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
